import React, { useCallback, useMemo, useState } from 'react';
import { Input, message, Modal, Skeleton, Spin, Tooltip, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toggleAddToExistingLinkModal } from '@redux/actions/modalAction';
import { AddToExistingLinkModalProps } from '@redux/types/modalType';
import {
  useListAssetReviewsQuery,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import LottieComponent from '@components/Lottie/LottieComponent';
import { useDebounced } from '@helpers/useDebounced';
import { AssetReviewItemDto } from '@api/Api';
import { CrossIcon, DropdownLinkIcon, SearchIcon, SuccessIcon } from '@icons';
import './AddToExistingLinkModal.less';

function AddToExistingLinkModal({
  visible,
  campaignId,
  assets,
  folders
}: AddToExistingLinkModalProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebounced(searchText, 400);

  const { mutateAsync: updateAssetReview, isPending: isUpdatingAssetReview } =
    useUpdateAssetReviewMutation({});

  const {
    data: assetReviewsData,
    isLoading: assetReviewsLoading,
    isFetchingNextPage: isFetchingNextPageAssetReviews,
    hasNextPage: hasNextPageAssetReviews,
    fetchNextPage: fetchNextPageAssetReviews
  } = useListAssetReviewsQuery({
    params: {
      searchQuery: debouncedSearchText,
      campaignId
    },
    enabled: visible && !!campaignId
  });

  const assetReviews = useMemo(() => {
    return assetReviewsData?.pages.flatMap((page) => page.edges);
  }, [assetReviewsData]);

  const onCloseModal = useCallback(() => {
    dispatch(toggleAddToExistingLinkModal({ visible: false, campaignId }));
  }, [dispatch, campaignId]);

  const onUpdateAssetReview = async (assetReview: AssetReviewItemDto) => {
    try {
      await updateAssetReview({
        reviewId: assetReview.id,
        ...(assets
          ? {
              assets: {
                add: assets
              }
            }
          : {}),
        ...(folders
          ? {
              folders: {
                add: folders
              }
            }
          : {})
      });
      const messageKey = 'add-to-existing-link-modal-success';
      message.success({
        key: messageKey,
        content: (
          <div className="message-dark-modal-content">
            <div className="message-dark-modal-inner">
              {assets?.length &&
                `${assets?.length} media added to share link “${assetReview.name}”`}
              {folders?.length &&
                `${folders?.length} folders added to share link “${assetReview.name}”`}
            </div>
            <Button
              onClick={() => {
                message.destroy(messageKey);
                navigate(`/campaigns/reviews/${campaignId}/${assetReview.id}`);
              }}
              type="primary"
            >
              View share link
            </Button>
            <CrossIcon
              onClick={() => message.destroy(messageKey)}
              className="message-dark-modal-close-btn"
            />
          </div>
        ),
        className: 'message-dark-modal add-to-existing-link-modal-success',
        icon: <SuccessIcon className="anticon" />
      });
      onCloseModal();
    } catch (error: any) {
      console.error(error);
      message.error({
        content:
          error?.response?.data?.message || 'Failed to add media to link',
        className: 'message-dark-modal'
      });
    }
  };

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={436}
      destroyOnClose
      zIndex={1008}
      onCancel={onCloseModal}
      closeIcon={<CrossIcon />}
      className="add-to-existing-link-modal"
      afterClose={() => dispatch(toggleAddToExistingLinkModal(null))}
    >
      <div className="add-to-existing-link-modal-label">existing links</div>
      <div className="add-to-existing-link-modal-search">
        <Input
          placeholder="Search links"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          prefix={<SearchIcon />}
          size="large"
        />
      </div>
      {isUpdatingAssetReview && (
        <div className="add-to-existing-link-modal-loading">
          <Spin />
        </div>
      )}
      <div className="add-to-existing-link-modal-infinite-scroll">
        <InfiniteScroll
          loading={
            isFetchingNextPageAssetReviews
              ? 'more'
              : assetReviewsLoading
              ? 'initial'
              : false
          }
          next={fetchNextPageAssetReviews}
          hasMore={hasNextPageAssetReviews ?? false}
          loader={
            <>
              <Skeleton.Input />
              <Skeleton.Input />
              <Skeleton.Input />
              <Skeleton.Input />
            </>
          }
        >
          {!assetReviewsLoading &&
            !assetReviews?.length &&
            !!debouncedSearchText && (
              <div className="empty_state empty_state--search">
                <LottieComponent className="icon" view="noSearchingResult" />
                <strong
                  className="main-body-text main-body-text--bold"
                  style={{ marginBottom: 8 }}
                >
                  No results found
                </strong>
                <p className="copy main-body-text main-body-text--dark-gray">
                  It seems we can’t find any results based on your search
                </p>
              </div>
            )}

          {!assetReviewsLoading &&
            !assetReviews?.length &&
            !debouncedSearchText && (
              <div className="empty_state empty_state--search">
                <LottieComponent className="icon" view="addMediaInFolder" />
                <strong
                  className="main-body-text main-body-text--bold"
                  style={{ marginBottom: 8 }}
                >
                  No links found
                </strong>
                <p className="copy main-body-text main-body-text--dark-gray">
                  Looks like there are no links in this project yet
                </p>
              </div>
            )}
          {assetReviews?.map((assetReview) => (
            <div
              key={assetReview.node.id}
              className="add-to-existing-link-modal-item"
              onClick={() => onUpdateAssetReview(assetReview.node)}
            >
              <DropdownLinkIcon />
              <Tooltip
                title={assetReview.node.name}
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
              >
                <div className="add-to-existing-link-modal-item-name">
                  {assetReview.node.name}
                </div>
              </Tooltip>
            </div>
          ))}
        </InfiniteScroll>
      </div>
    </Modal>
  );
}

export default AddToExistingLinkModal;
